import { useEffect, useState, type RefObject } from 'react'

/**
 * Format d'affichage d'une liste (maquettes « RECA Listes ») :
 * - `desktop` : toutes les colonnes;
 * - `tablet` : colonnes repliées par priorité dans une ligne dépliable (chevron 44 px);
 * - `portrait` : pile de cartes-lignes.
 *
 * Le format dépend de la largeur réelle de la liste, pas de celle de la fenêtre : la navigation
 * latérale ouverte ou fermée change la place disponible. Les colonnes de priorité 3, puis 2, se
 * replient avant que le tableau ne déborde; aucune police n'est réduite.
 */
export type ListLayout = 'desktop' | 'tablet' | 'portrait'

export interface ListLayoutResolution {
  layout: ListLayout
  /** Priorité maximale des colonnes affichées dans la grille. */
  maxPriority: 1 | 2 | 3
}

export interface ListLayoutColumn {
  width: string
  priority: 1 | 2 | 3
}

/** Gouttière entre colonnes (`--space-4`), marge intérieure des lignes, bouton de repli. */
export const COLUMN_GAP = 16
const ROW_PADDING = 32
const FOLD_BUTTON = 52
/** Hors desktop, les boutons d'action passent à 44 px : la colonne Actions s'élargit d'autant (`ListTable`). */
const TABLET_ACTIONS_GROWTH = 16
const DEFAULT_MIN = 120
/** En deçà, même les colonnes de priorité 1 ne laissent plus de place lisible : cartes. */
const PORTRAIT_MAX = 600

/** Largeur minimale d'une piste de grille : `minmax(160px,1fr)` → 160, `92px` → 92. */
export function minimumTrackWidth(track: string): number {
  const match = /^(?:minmax\(\s*)?(\d+(?:\.\d+)?)px/.exec(track.trim())
  return match?.[1] ? Number(match[1]) : DEFAULT_MIN
}

function requiredWidth(
  columns: readonly ListLayoutColumn[],
  actionsWidth: number,
  maxPriority: number,
): number {
  const shown = columns.filter((column) => column.priority <= maxPriority)
  const folded = shown.length < columns.length
  const tracks = shown.reduce((sum, column) => sum + minimumTrackWidth(column.width), 0)
  // Seul l'affichage complet reste au format desktop; tout autre format élargit la colonne Actions.
  const actions = actionsWidth + (maxPriority < 3 ? TABLET_ACTIONS_GROWTH : 0) + (folded ? FOLD_BUTTON : 0)
  return tracks + actions + COLUMN_GAP * shown.length + ROW_PADDING
}

export function resolveListLayout(
  width: number | null,
  columns: readonly ListLayoutColumn[],
  actionsWidth: number,
): ListLayoutResolution {
  // Largeur inconnue (premier rendu, environnement sans mesure) : rendu complet.
  if (width === null || width <= 0) return { layout: 'desktop', maxPriority: 3 }
  if (requiredWidth(columns, actionsWidth, 3) <= width) return { layout: 'desktop', maxPriority: 3 }
  if (width < PORTRAIT_MAX) return { layout: 'portrait', maxPriority: 1 }
  if (requiredWidth(columns, actionsWidth, 2) <= width) return { layout: 'tablet', maxPriority: 2 }
  if (requiredWidth(columns, actionsWidth, 1) <= width) return { layout: 'tablet', maxPriority: 1 }
  return { layout: 'portrait', maxPriority: 1 }
}

/** Largeur d'un élément, suivie par `ResizeObserver`; `null` tant qu'elle n'est pas mesurée. */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number | null {
  const [width, setWidth] = useState<number | null>(null)

  useEffect(() => {
    const element = ref.current
    if (!element || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const next = Math.round(entry.contentRect.width)
      setWidth((previous) => (previous === next ? previous : next))
    })
    observer.observe(element)
    return () => {
      observer.disconnect()
    }
  }, [ref])

  return width
}
